import React from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../../../Admin/constants/adminColors';
import SectionCard from '../SectionCard';
import CrossPlatformDropdown from '../../../../../../components/CrossPlatformDropdown';
import { NewActionDraft, CoachingItemCategory } from '../../types/beginActivity.common.types';
import {
  selectPeopleportActionTypeOptions,
  selectPeopleportActionFocusOptionsByType,
} from '../../../../../../store/slices/masterDataSlice';
import { NewActionProps } from './NewAction.types';
import styles from './NewAction.styles';

// ─── Fallback focus area options (used until /common/master-data loads) ───────
const FALLBACK_METRIC_FOCUS_OPTIONS: string[] = [
  'Gap Time',
  'DPS',
  'KU',
  'BPA',
  'Cases Picked',
];

const FALLBACK_PROCESS_FOCUS_OPTIONS: string[] = [
  'Replenishment Standards',
  'Dock Management',
  'Repack Policy',
  'Preferred Methods',
  'PIT Safety',
  'Geo Loading',
  'Trailer Loading',
  'Other',
];

const FALLBACK_ACTION_TYPE_OPTIONS: CoachingItemCategory[] = ['Metric', 'Process'];

const ACTION_TYPE_DESCRIPTIONS: Record<string, string> = {
  Metric: 'KPI based improvement',
  Process: 'Operational standard or policy',
};

function getFocusOptions(
  type: CoachingItemCategory | '',
  focusOptionsByType: Record<string, string[]>,
): string[] {
  if (!type) return [];
  const fromApi = focusOptionsByType[type];
  if (fromApi && fromApi.length > 0) return fromApi;
  if (type === 'Metric') return FALLBACK_METRIC_FOCUS_OPTIONS;
  if (type === 'Process') return FALLBACK_PROCESS_FOCUS_OPTIONS;
  return [];
}

function emptyDraft(): NewActionDraft {
  return { type: '', focus: '', otherFocus: '', opportunity: '', plan: '' };
}

// ─── Validation ───────────────────────────────────────────────────────────────
function validateDraft(draft: NewActionDraft): string | null {
  if (!draft.type) return null; // form intentionally blank
  if (!draft.focus) return 'Select a Focus Area for the new action item.';
  if (draft.type === 'Process' && draft.focus === 'Other') {
    const len = draft.otherFocus.trim().length;
    if (len < 5 || len > 25) return 'Other Process Focus must be 5 to 25 characters.';
  }
  if (draft.opportunity.trim().length < 10)
    return 'Opportunity Identified must contain at least 10 characters.';
  if (draft.plan.trim().length < 10)
    return 'Action Plan must contain at least 10 characters.';
  return null;
}

// ─── Main component ───────────────────────────────────────────────────────────
const NewAction: React.FC<NewActionProps> = ({
  draft,
  onDraftChange,
  activityType,
  recommendedMetricFocuses = [],
  recommendedProcessFocuses = [],
  isGated = false,
  gateMessage,
  noOpenItem = false,
  support = '',
  onSupportChange,
}) => {
  const isPulseCheck = activityType === 'pulse_check';
  const closeLabel = isPulseCheck ? 'Pulse Check' : 'Observation';

  const apiTypeOptions = useSelector(selectPeopleportActionTypeOptions);
  const focusOptionsByType = useSelector(selectPeopleportActionFocusOptionsByType);
  const actionTypes = (apiTypeOptions.length > 0 ? apiTypeOptions : FALLBACK_ACTION_TYPE_OPTIONS).map(
    (value) => ({
      value: value as CoachingItemCategory,
      description: ACTION_TYPE_DESCRIPTIONS[value] ?? '',
    }),
  );

  const update = (partial: Partial<NewActionDraft>) =>
    onDraftChange({ ...draft, ...partial });

  const handleTypeSelect = (type: CoachingItemCategory) => {
    if (draft.type === type) {
      onDraftChange(emptyDraft());
    } else {
      onDraftChange({ ...emptyDraft(), type });
    }
  };

  const focusOptions = getFocusOptions(draft.type, focusOptionsByType).map((v) => ({
    id: v,
    displayText: v,
  }));

  const selectedFocus = draft.focus
    ? { id: draft.focus, displayText: draft.focus }
    : null;

  const showOtherField =
    draft.type === 'Process' && draft.focus === 'Other';

  const showRecommendedMetrics =
    draft.type === 'Metric' && recommendedMetricFocuses.length > 0;

  const showRecommendedProcess =
    draft.type === 'Process' && recommendedProcessFocuses.length > 0;

  return (
    <SectionCard title="New Action" subtitle="Action Type drives the form">
      <View style={styles.body}>
        {/* ── Gate banner — icon + bold title + body (no border box) ───── */}
        <View style={styles.gateBanner}>
          <View style={styles.gateIcon}>
            <MaterialIcons
              name={isGated ? 'lock' : 'check-circle'}
              size={18}
              color={isGated ? AdminColors.textSecondary : AdminColors.green600}
            />
          </View>
          <View style={styles.gateTextBlock}>
            <Text style={styles.gateTitle}>
              {isGated
                ? 'Review the open coaching item first.'
                : noOpenItem
                ? 'No coaching item is open.'
                : isPulseCheck
                ? 'Select an Action Type (optional)'
                : 'Select an Action Type'}
            </Text>
            <Text style={styles.gateBody}>
              {isGated
                ? (gateMessage ?? 'Select Shows Improvement, No Improvement Shown, or Complete. Only one coaching item is open at a time.')
                : noOpenItem
                ? `You may open the next coaching item below, or close the ${closeLabel} without adding one. An employee has one open coaching item at a time.`
                : isPulseCheck
                ? 'Leave blank to close without recording a new action.'
                : 'Select an action type to display the appropriate fields.'}
            </Text>
          </View>
        </View>

        {/* ── Step 1: Action Type picker ────────────────────────────── */}
        {!isGated && (
          <View>
            <Text style={styles.stepLabel}>
              {isPulseCheck
                ? '1. Select Action Type (optional)'
                : '1. Select Action Type'}
            </Text>
            <View style={styles.typePicker}>
              {actionTypes.map(({ value, description }) => {
                const isActive = draft.type === value;
                return (
                  <TouchableOpacity
                    key={value}
                    style={[styles.typeCard, isActive && styles.typeCardActive]}
                    onPress={() => handleTypeSelect(value)}
                    accessibilityRole="button"
                    accessibilityState={{ selected: isActive }}
                    accessibilityLabel={`${value}: ${description}`}
                  >
                    <Text
                      style={[
                        styles.typeCardTitle,
                        isActive && styles.typeCardTitleActive,
                      ]}
                    >
                      {value}
                    </Text>
                    <Text style={styles.typeCardDesc}>{description}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        {/* Placeholder: no type chosen yet */}
        {!isGated && !draft.type && (
          <Text style={styles.placeholder}>
            Select an Action Type above to display the appropriate fields, or leave
            this blank to close without a new action.
          </Text>
        )}

        {/* ── Recommended chips ─────────────────────────────────────── */}
        {!isGated && showRecommendedMetrics && (
          <View style={styles.recommendSection}>
            <Text style={styles.recommendLabel}>Recommended focus</Text>
            <Text style={styles.recommendHint}>
              Missed in more than a quarter of the last 12 weeks
            </Text>
            <View style={styles.chipsRow}>
              {recommendedMetricFocuses.map((focus) => {
                const isActive = draft.focus === focus;
                return (
                  <TouchableOpacity
                    key={focus}
                    style={[styles.chip, isActive && styles.chipActive]}
                    onPress={() => update({ focus, otherFocus: '' })}
                    accessibilityRole="button"
                    accessibilityState={{ selected: isActive }}
                  >
                    <Text
                      style={[styles.chipText, isActive && styles.chipTextActive]}
                    >
                      {focus}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        {!isGated && showRecommendedProcess && (
          <View style={styles.recommendSection}>
            <Text style={styles.recommendLabel}>Recommended focus</Text>
            <Text style={styles.recommendHint}>
              Most selected on this employee's observations over the last 12 months
            </Text>
            <View style={styles.chipsRow}>
              {recommendedProcessFocuses.map(({ focus, text, count }) => {
                const isActive = draft.focus === focus;
                return (
                  <TouchableOpacity
                    key={text}
                    style={[
                      styles.chip,
                      styles.chipObservation,
                      isActive && styles.chipObservationActive,
                    ]}
                    onPress={() => update({ focus, otherFocus: '' })}
                    accessibilityRole="button"
                    accessibilityState={{ selected: isActive }}
                    accessibilityLabel={`${text} — selected ${count} time${count !== 1 ? 's' : ''}`}
                  >
                    <Text
                      style={[styles.chipText, isActive && styles.chipTextActive]}
                    >
                      {text}
                    </Text>
                    <Text style={styles.chipSubText}>
                      {focus} | selected {count} {count === 1 ? 'time' : 'times'}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        {/* ── Step 2: Focus Area ────────────────────────────────────── */}
        {!isGated && !!draft.type && (
          <View style={styles.fieldGroup}>
            <View>
              <Text style={styles.fieldLabel}>2. Focus Area</Text>
              <CrossPlatformDropdown
                childList={focusOptions}
                label=""
                placeholder="Select a focus area"
                selection="single"
                selectedValue={selectedFocus}
                setSelectedValue={(v: any) => {
                  const chosen = Array.isArray(v) ? v[0] : v;
                  update({ focus: chosen?.id ?? '', otherFocus: '' });
                }}
                size="small"
                isClearable={false}
              />
            </View>

            {showOtherField && (
              <View>
                <Text style={styles.fieldLabel}>
                  Other Process Focus, 5 to 25 characters
                </Text>
                <Text style={styles.fieldHint}>
                  Required when Other is selected. It is a focus name, not a description.
                </Text>
                <TextInput
                  style={[
                    styles.textInput,
                    draft.otherFocus.trim().length > 0 &&
                      (draft.otherFocus.trim().length < 5 ||
                        draft.otherFocus.trim().length > 25) &&
                      styles.textInputError,
                  ]}
                  value={draft.otherFocus}
                  onChangeText={(text) => update({ otherFocus: text })}
                  placeholder="Name the process focus"
                  placeholderTextColor="#B1B1B1"
                  maxLength={25}
                  accessibilityLabel="Other process focus"
                />
              </View>
            )}
          </View>
        )}

        {/* ── Steps 3 & 4: Opportunity + Action Plan ────────────────── */}
        {!isGated && !!draft.type && (
          <View style={styles.fieldGroup}>
            <View>
              <Text style={styles.fieldLabel}>
                3. Opportunity Identified, minimum 10 characters
              </Text>
              <Text style={styles.fieldHint}>At least 10 characters are required.</Text>
              <TextInput
                style={[
                  styles.textArea,
                  draft.opportunity.length > 0 &&
                    draft.opportunity.trim().length < 10 &&
                    styles.textAreaError,
                ]}
                value={draft.opportunity}
                onChangeText={(text) => update({ opportunity: text })}
                placeholder="What behavior, metric, or process opportunity was identified?"
                placeholderTextColor="#B1B1B1"
                multiline
                textAlignVertical="top"
                accessibilityLabel="Opportunity identified"
              />
              {draft.opportunity.length > 0 &&
                draft.opportunity.trim().length < 10 && (
                  <Text style={styles.charHint}>
                    At least 10 characters required (
                    {draft.opportunity.trim().length} / 10)
                  </Text>
                )}
            </View>

            <View>
              <Text style={styles.fieldLabel}>
                4. Action Plan, minimum 10 characters
              </Text>
              <Text style={styles.fieldHint}>At least 10 characters are required.</Text>
              <TextInput
                style={[
                  styles.textArea,
                  draft.plan.length > 0 &&
                    draft.plan.trim().length < 10 &&
                    styles.textAreaError,
                ]}
                value={draft.plan}
                onChangeText={(text) => update({ plan: text })}
                placeholder="What will the employee do, and what does better look like?"
                placeholderTextColor="#B1B1B1"
                multiline
                textAlignVertical="top"
                accessibilityLabel="Action plan"
              />
              {draft.plan.length > 0 && draft.plan.trim().length < 10 && (
                <Text style={styles.charHint}>
                  At least 10 characters required ({draft.plan.trim().length} / 10)
                </Text>
              )}
            </View>
          </View>
        )}

        {/* ── Step 5: Support Needed (optional) ────────────────────── */}
        {!isGated && !!draft.type && onSupportChange && (
          <View>
            <Text style={styles.fieldLabel}>5. Support Needed, optional</Text>
            <Text style={styles.fieldHint}>
              Equipment, training, job shadowing, schedule coordination, or process
              clarification.
            </Text>
            <TextInput
              style={styles.textArea}
              value={support}
              onChangeText={onSupportChange}
              placeholder="What does the employee need from the supervisor, site, or team?"
              placeholderTextColor="#B1B1B1"
              multiline
              textAlignVertical="top"
              accessibilityLabel="Support needed"
            />
          </View>
        )}
      </View>
    </SectionCard>
  );
};

export default NewAction;
